import { NgClass } from '@angular/common';
import { Component, computed, DestroyRef, effect, inject, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatTooltip } from '@angular/material/tooltip';
import { AppInsightsService, AppInsightsServiceInjector } from '@next/core-lib/app-insights';
import { DIALOG_RETURN_TYPES, DialogService } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { distinctUntilChanged } from 'rxjs';
import { toUtcDate } from 'src/app/shared/utils';

import { phytophthoraPressureOptions } from 'src/app/blight-v2/shared/helpers/blight-helpers';
import {
  PlantDateWarningComponent,
} from 'src/app/shared/components/plant-date-warning.component/plant-date-warning.component';
import {
  getPlantDateLimits,
  getPlantDateWarningCodes,
} from 'src/app/shared/components/plant-date-warning.component/plant-date-warning.helpers';
import { DateService } from 'src/app/shared/services/date.service';

import { CropFieldRegistration, PhytophthoraPressure } from '../../../../../../../blight-v2/models/models';
import { BlightAIIdentifiers } from '../../../../../../../blight-v2/shared/constants';
import { BlightSignalStore } from '../../../../../../signal-store/blight-signal-store';

@Component({
  selector: 'app-blight-details-toolbar',
  templateUrl: './blight-details-toolbar.component.html',
  styleUrls: ['./blight-details-toolbar.component.scss'],
  imports: [
    MatFormField,
    MatTooltip,
    NgClass,
    MatLabel,
    MatInput,
    MatDatepickerInput,
    FormsModule,
    ReactiveFormsModule,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatSelect,
    MatOption,
    MatIcon,
    TranslatePipe,
    PlantDateWarningComponent,
  ],
})
export class BlightInsightToolbarComponent {
  private readonly store = inject(BlightSignalStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(DialogService);
  private readonly locale = inject(LocaleService);
  private readonly ai = inject<AppInsightsService>(AppInsightsServiceInjector);
  private readonly dateService = inject(DateService);

  readonly selectedCropField = input.required<CropFieldRegistration>();
  readonly isParticipatingToBem = input(false);
  protected readonly hasBlightAdvices = input<boolean>(false);
  readonly isReadOnly = input(false);

  protected readonly phytophthoraPressureOptions = phytophthoraPressureOptions;

  protected adviceDateMin: Date | undefined;
  protected readonly plantDateLimits = getPlantDateLimits(this.dateService);
  protected emergenceDateMin: Date | undefined;
  protected emergenceDateMax: Date | undefined;
  protected harvestDateMin: Date | undefined;
  protected harvestDateMax: Date | undefined;

  protected plantingDateFormControl = new FormControl();
  protected emergenceDateFormControl = new FormControl();
  protected harvestDateFormControl = new FormControl();
  protected pressureFormControl = new FormControl();
  protected suppressSprayAdviceFormControl = new FormControl(false);
  protected readonly plantDateWarningCodes = computed(() => {
    const field = this.selectedCropField();

    return getPlantDateWarningCodes({
      plantDate: field.plantDate,
      plantDateCreatedAt: field.plantDateCreatedAt,
      plantDateUpdatedAt: field.plantDateUpdatedAt,
      isParticipatingToBem: this.isParticipatingToBem(),
    });
  });

  protected readonly disabledTooltipText = computed(() => {
    if (this.isReadOnly()) {
      return 'UI.CurrentYearOnlyMutationMessage';
    }

    if (this.selectedCropField().isPlantDateConfirmed) {
      return 'UI.PlantDateConfirmedMessage';
    }

    return this.isParticipatingToBem() ? 'UI.PlantDateBemMessage' : '';
  });
  protected readonly isPlantDateDisabled = computed(
    () => this.isReadOnly() || this.selectedCropField().isPlantDateConfirmed || this.isParticipatingToBem(),
  );

  constructor() {
    effect(() => {
      if (this.isPlantDateDisabled()) {
        this.plantingDateFormControl.disable({ emitEvent: false });
      } else {
        this.plantingDateFormControl.enable({ emitEvent: false });
      }
    });

    effect(() => {
      const isReadOnly = this.isReadOnly();
      const canUpdateBlightAdvice = this.hasBlightAdvices() && !isReadOnly;

      if (canUpdateBlightAdvice) {
        this.suppressSprayAdviceFormControl.enable({ emitEvent: false });
        this.pressureFormControl.enable({ emitEvent: false });
      } else {
        this.suppressSprayAdviceFormControl.disable({ emitEvent: false });
        this.pressureFormControl.disable({ emitEvent: false });
      }

      if (isReadOnly) {
        this.emergenceDateFormControl.disable({ emitEvent: false });
        this.harvestDateFormControl.disable({ emitEvent: false });
      } else {
        this.emergenceDateFormControl.enable({ emitEvent: false });
        this.harvestDateFormControl.enable({ emitEvent: false });
      }
    });

    effect(() => {
      const cropFieldRegistration = this.selectedCropField();
      this.adviceDateMin = cropFieldRegistration.plantDate;

      this.plantingDateFormControl.setValue(cropFieldRegistration.plantDate, { emitEvent: false });
      this.emergenceDateFormControl.setValue(cropFieldRegistration.emergenceDate, { emitEvent: false });
      this.harvestDateFormControl.setValue(cropFieldRegistration.harvestDate, { emitEvent: false });
      this.suppressSprayAdviceFormControl.setValue(cropFieldRegistration.suppressSprayAdvice ?? false, { emitEvent: false });
      this.pressureFormControl.setValue(cropFieldRegistration.diseasePressure, { emitEvent: false });
    });

    this.subscribeToPlantingDateChanged();
    this.subscribeToEmergenceDateChanged();
    this.subscribeToHarvestDateChanged();
    this.subscribeToPressureChange();
    this.subscribeToSuppressSprayAdviceFormControlChanged();
  }

  private subscribeToPlantingDateChanged(): void {
    this.plantingDateFormControl.valueChanges
      .pipe(takeUntilDestroyed(), distinctUntilChanged())
      .subscribe((newDate) => {
        const plantingDate = toUtcDate(newDate);
        if (!plantingDate || !this.plantingDateFormControl.valid || isSameDate(plantingDate, this.selectedCropField().plantDate)) {
          return;
        }

        this.store.updatePlantDate({
          date: plantingDate,
          cropFieldId: this.selectedCropField().cropFieldId,
          year: this.selectedCropField().year,
        });
      });
  }

  private subscribeToEmergenceDateChanged(): void {
    this.emergenceDateFormControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((newDate) => {
        const emergenceDate = toUtcDate(newDate);
        if (!emergenceDate || !this.emergenceDateFormControl.valid || isSameDate(emergenceDate, this.selectedCropField().emergenceDate)) {
          return;
        }

        this.ai.trackEvent(BlightAIIdentifiers.changeEmergence, {});
        this.store.updateEmergenceDate({
          date: emergenceDate,
          cropFieldId: this.selectedCropField().cropFieldId,
          year: this.selectedCropField().year,
        });
      });
  }

  private subscribeToHarvestDateChanged(): void {
    this.harvestDateFormControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((newDate) => {
        const harvestDate = toUtcDate(newDate);
        if (!harvestDate || !this.harvestDateFormControl.valid || isSameDate(harvestDate, this.selectedCropField().harvestDate)) {
          return;
        }

        this.store.updateHarvestDate({
          date: harvestDate,
          cropFieldId: this.selectedCropField().cropFieldId,
          year: this.selectedCropField().year,
        });
      });
  }

  private subscribeToSuppressSprayAdviceFormControlChanged(): void {
    this.suppressSprayAdviceFormControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((suppressSprayAdvice) => {
        const selectedCropField = this.selectedCropField();
        if (
          suppressSprayAdvice === selectedCropField.suppressSprayAdvice ||
          !this.suppressSprayAdviceFormControl.valid
        ) {
          return;
        }

        if (suppressSprayAdvice) {
          this.ai.trackEvent(BlightAIIdentifiers.delayFirst, { suppressSprayAdvice });
          this.dialog
            .confirm({
              title: this.locale.translate('blight.blightDetail.delayFirstSprayConfirmTitle'),
            })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((result) => {
              if (result.status === DIALOG_RETURN_TYPES.confirm) {
                this.dispatchSuppressSprayAdvice(suppressSprayAdvice);
              } else {
                this.suppressSprayAdviceFormControl.setValue(false, { emitEvent: false });
              }
            });
          return;
        }

        if (suppressSprayAdvice !== null) {
          this.dispatchSuppressSprayAdvice(suppressSprayAdvice);
        }
      });
  }

  private subscribeToPressureChange(): void {
    this.pressureFormControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((pressure: PhytophthoraPressure) => {
        const selectedCropField = this.selectedCropField();
        if (pressure === selectedCropField.diseasePressure) {
          return;
        }

        this.store.updatePhytophthoraPressure({
          phytophthoraPressure: pressure,
          cropFieldId: selectedCropField.cropFieldId,
          year: selectedCropField.year,
        });
      });
  }

  protected openDatePicker(
    datePicker: MatDatepicker<Date>,
    control: FormControl,
    event?: Event,
  ): void {
    if (control.disabled || (control === this.plantingDateFormControl && this.isPlantDateDisabled())) {
      return;
    }

    event?.preventDefault();
    datePicker.open();
  }

  private dispatchSuppressSprayAdvice(suppressSprayAdvice: boolean): void {
    this.store.updateSuppressSprayAdvice({
      suppressSprayAdvice,
      cropFieldId: this.selectedCropField().cropFieldId,
      year: this.selectedCropField().year,
    });
  }
}

const isSameDate = (left: Date | null | undefined, right: Date | null | undefined): boolean => {
  if (!left || !right) {
    return left === right;
  }

  return (
    left.getUTCFullYear() === right.getUTCFullYear() &&
    left.getUTCMonth() === right.getUTCMonth() &&
    left.getUTCDate() === right.getUTCDate()
  );
};
